import { useState, useEffect, useRef } from 'react';
import { formatCurrency, formatDate } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Printer, Download, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import html2pdf from 'html2pdf.js';

// Company Info - TRANSTAR SRL
const COMPANY_INFO = {
  name: 'TRANSTAR SRL',
  rnc: '131-072623',
  address: 'Abraham Lincoln esq. Lope de Vega, Plaza Castilla, Local 1B, Santo Domingo, Republica Dominicana',
  phone: '829-870-0339',
  email: 'info@transtar.io',
  logo: 'https://customer-assets.emergentagent.com/job_teamflow-manage-1/artifacts/krciwypq_2024%20Logo%20TRANSTAR%20.jpg'
};

export default function QuotePrint({ quote, open, onClose }) {
  const [downloading, setDownloading] = useState(false);
  const printRef = useRef(null);

  const handlePrint = () => {
    const printContent = printRef.current;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Cotización ${quote?.quote_number}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: Arial, sans-serif; color: #333; }
            ${printStyles}
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const handleDownloadPDF = async () => {
    if (!printRef.current || !quote) return;
    
    setDownloading(true);
    
    try {
      const element = printRef.current;
      const quoteNumber = quote.quote_number.replace(/[^a-zA-Z0-9]/g, '-');
      const clientName = quote.client_name?.replace(/[^a-zA-Z0-9]/g, '-') || 'Cliente';
      const fileName = `Cotizacion-${quoteNumber}-${clientName}.pdf`;
      
      const opt = {
        margin: [10, 10, 10, 10],
        filename: fileName,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { 
          scale: 2,
          useCORS: true,
          letterRendering: true,
          logging: false
        },
        jsPDF: { 
          unit: 'mm', 
          format: 'a4', 
          orientation: 'portrait'
        },
        pagebreak: { mode: 'avoid-all' }
      };

      await html2pdf().set(opt).from(element).save();
      toast.success('PDF descargado exitosamente');
    } catch (err) {
      console.error('Error generating PDF:', err);
      toast.error('Error al generar PDF');
    } finally {
      setDownloading(false);
    }
  };

  if (!open || !quote) return null;

  const validUntil = quote.valid_until ? formatDate(quote.valid_until) : null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0">
        <DialogHeader className="p-4 border-b flex flex-row items-center justify-between">
          <DialogTitle className="font-heading text-xl">Vista de Cotización</DialogTitle>
          <div className="flex items-center gap-2">
            <Button 
              onClick={handleDownloadPDF} 
              variant="default"
              className="gap-2 bg-emerald-600 hover:bg-emerald-700"
              disabled={downloading}
              data-testid="download-quote-pdf-btn"
            >
              {downloading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              {downloading ? 'Generando...' : 'PDF'}
            </Button>
            <Button onClick={handlePrint} variant="outline" className="gap-2">
              <Printer className="h-4 w-4" />
              Imprimir
            </Button>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>
        
        <div ref={printRef} className="p-8 bg-white">
          {/* Header */}
          <div className="flex justify-between items-start mb-8">
            <div>
              <img src={COMPANY_INFO.logo} alt="TRANSTAR" className="h-20 w-auto mb-2" crossOrigin="anonymous" />
              <p className="text-sm text-gray-600 font-semibold">RNC: {COMPANY_INFO.rnc}</p>
            </div>
            
            <div className="text-right">
              <h2 className="text-3xl font-bold text-gray-800 mb-2">COTIZACION</h2>
              <p className="text-lg font-mono font-semibold text-[#1e3a5f]">{quote.quote_number}</p>
              <p className="text-sm text-gray-500 mt-1">Fecha: {formatDate(quote.created_at)}</p>
            </div>
          </div>

          {/* Status Banner */}
          <div className="bg-[#1e3a5f] text-white p-4 rounded-lg mb-6">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm opacity-90">Estado de Cotización</p>
                <p className="text-xl font-semibold">
                  {quote.status === 'sent' ? 'Enviada' : 
                   quote.status === 'approved' ? 'Aprobada' :
                   quote.status === 'accepted' ? 'Aceptada por Cliente' :
                   quote.status === 'rejected' ? 'Rechazada' :
                   quote.status === 'converted' ? 'Convertida a Factura' : 'Borrador'}
                </p>
              </div>
              {validUntil && (
                <div className="text-right">
                  <p className="text-sm opacity-90">Válida hasta</p>
                  <p className="font-semibold">{validUntil}</p>
                </div>
              )}
            </div>
          </div>

          {/* Client Info */}
          <div className="grid grid-cols-2 gap-8 mb-8">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-semibold text-gray-700 mb-2 uppercase text-sm tracking-wide">Cliente:</h3>
              <p className="font-semibold text-lg">{quote.client_name}</p>
              {quote.client_email && <p className="text-sm text-gray-600">{quote.client_email}</p>}
            </div>
            
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-semibold text-gray-700 mb-2 uppercase text-sm tracking-wide">Preparado por:</h3>
              <p className="font-semibold">{COMPANY_INFO.name}</p>
              <p className="text-sm text-gray-600">Equipo de Ventas</p>
            </div>
          </div>

          {/* Items Table */}
          <div className="mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-800 text-white">
                  <th className="p-3 text-left">Descripción</th>
                  <th className="p-3 text-center w-20">Cant.</th>
                  <th className="p-3 text-right w-32">Precio Unit.</th>
                  <th className="p-3 text-right w-32">Total</th>
                </tr>
              </thead>
              <tbody>
                {quote.items?.map((item, index) => (
                  <tr key={index} className="border-b">
                    <td className="p-3">{item.description}</td>
                    <td className="p-3 text-center">{item.quantity}</td>
                    <td className="p-3 text-right font-mono">{formatCurrency(item.unit_price)}</td>
                    <td className="p-3 text-right font-mono font-semibold">
                      {formatCurrency(item.quantity * item.unit_price)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals - NO COSTS SHOWN (client view) */}
          <div className="flex justify-end mb-8">
            <div className="w-72">
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">Subtotal:</span>
                <span className="font-mono">{formatCurrency(quote.subtotal)}</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">ITBIS (18%):</span>
                <span className="font-mono">{formatCurrency(quote.tax)}</span>
              </div>
              <div className="flex justify-between py-3 bg-[#1e3a5f] text-white px-3 rounded-lg mt-2">
                <span className="font-semibold">TOTAL:</span>
                <span className="font-mono font-bold text-xl">{formatCurrency(quote.total)}</span>
              </div>
            </div>
          </div>

          {/* Notes */}
          {quote.notes && (
            <div className="bg-gray-50 p-4 rounded-lg mb-8">
              <h3 className="font-semibold text-gray-700 mb-2 uppercase text-sm tracking-wide">Notas:</h3>
              <p className="text-gray-600">{quote.notes}</p>
            </div>
          )}

          {/* Terms */}
          <div className="bg-blue-50 p-4 rounded-lg mb-8 border border-blue-200">
            <h3 className="font-semibold text-blue-800 mb-2 uppercase text-sm tracking-wide">Términos y Condiciones:</h3>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• Esta cotización tiene una validez de 15 días a partir de la fecha de emisión.</li>
              <li>• Los precios no incluyen impuestos adicionales a menos que se indique lo contrario.</li>
              <li>• El tiempo de entrega será acordado una vez confirmada la orden.</li>
              <li>• Forma de pago: 50% anticipo, 50% contra entrega.</li>
            </ul>
          </div>

          {/* Footer */}
          <div className="border-t pt-6 text-center text-sm text-gray-500">
            <p className="font-semibold mb-1">Gracias por considerarnos!</p>
            <p className="font-medium text-gray-600">{COMPANY_INFO.name} | RNC: {COMPANY_INFO.rnc}</p>
            <p>{COMPANY_INFO.address}</p>
            <p>Tel: {COMPANY_INFO.phone} | {COMPANY_INFO.email}</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const printStyles = `
  .p-8 { padding: 2rem; }
  .mb-8 { margin-bottom: 2rem; }
  .mb-6 { margin-bottom: 1.5rem; }
  .mb-2 { margin-bottom: 0.5rem; }
  .mt-2 { margin-top: 0.5rem; }
  .mt-1 { margin-top: 0.25rem; }
  .gap-4 { gap: 1rem; }
  .gap-8 { gap: 2rem; }
  .flex { display: flex; }
  .grid { display: grid; }
  .grid-cols-2 { grid-template-columns: repeat(2, 1fr); }
  .items-start { align-items: flex-start; }
  .items-center { align-items: center; }
  .justify-between { justify-content: space-between; }
  .justify-center { justify-content: center; }
  .justify-end { justify-content: flex-end; }
  .text-right { text-align: right; }
  .text-center { text-align: center; }
  .text-left { text-align: left; }
  .font-bold { font-weight: bold; }
  .font-semibold { font-weight: 600; }
  .font-medium { font-weight: 500; }
  .font-mono { font-family: monospace; }
  .text-2xl { font-size: 1.5rem; }
  .text-3xl { font-size: 1.875rem; }
  .text-xl { font-size: 1.25rem; }
  .text-lg { font-size: 1.125rem; }
  .text-sm { font-size: 0.875rem; }
  .text-xs { font-size: 0.75rem; }
  .uppercase { text-transform: uppercase; }
  .tracking-wide { letter-spacing: 0.025em; }
  .text-white { color: white; }
  .text-gray-800 { color: #1f2937; }
  .text-gray-700 { color: #374151; }
  .text-gray-600 { color: #4b5563; }
  .text-gray-500 { color: #6b7280; }
  .text-blue-800 { color: #1e40af; }
  .text-blue-700 { color: #1d4ed8; }
  .bg-white { background-color: white; }
  .bg-gray-50 { background-color: #f9fafb; }
  .bg-gray-800 { background-color: #1f2937; }
  .bg-blue-50 { background-color: #eff6ff; }
  .rounded-lg { border-radius: 0.5rem; }
  .border { border: 1px solid #e5e7eb; }
  .border-b { border-bottom: 1px solid #e5e7eb; }
  .border-t { border-top: 1px solid #e5e7eb; }
  .border-blue-200 { border-color: #bfdbfe; }
  .p-3 { padding: 0.75rem; }
  .p-4 { padding: 1rem; }
  .px-3 { padding-left: 0.75rem; padding-right: 0.75rem; }
  .py-2 { padding-top: 0.5rem; padding-bottom: 0.5rem; }
  .py-3 { padding-top: 0.75rem; padding-bottom: 0.75rem; }
  .pt-6 { padding-top: 1.5rem; }
  .w-full { width: 100%; }
  .w-72 { width: 18rem; }
  .w-20 { width: 5rem; }
  .w-32 { width: 8rem; }
  .h-20 { height: 5rem; }
  .w-auto { width: auto; }
  .space-y-1 > * + * { margin-top: 0.25rem; }
  .opacity-90 { opacity: 0.9; }
  table { border-collapse: collapse; }
  img { max-width: 100%; height: auto; }
  ul { list-style: none; padding: 0; }
`;
